import React, { useState, useEffect, useRef } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, TextInput, Dimensions, Image, Platform, ScrollView } from 'react-native';
import { Ionicons, MaterialIcons,} from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { colores, fuentes, layout } from '../../../styles/variablesCss';
import PlatformDetector from '../../../utils/PlatformDetector';
import { useUser } from '../../../context/UserContext';
import { useCart } from '../../../context/CartContext';
import DesplegableProductos from './DesplegableProductos';
import Login from './AdminUsuario/Login';
import Categorias from './Categorias';
import CategoriasMobil from './CategoriasMobil';
import { useProducts } from '../../../context/ProductContext';
import BannerBotonesHeader from '../Body/Banners/BannerBotonesHeader';
import BannerManager from '../../../components/BannerManager';
import Carrito from '../../Home/Header/Carrito/Carrito';
import { formatPrecio } from '../../../utils/formatPrice';
import { trackUserLogin } from '../../../utils/analytics';

const HeaderRojo = () => {
  const { nombreUsuario, codUsu, mail } = useUser();
  const navigation = useNavigation();
  const route = useRoute();

  // Verificar si estamos en la pantalla de checkout
  const isFinalizarCompra = route.name === 'CheckoutFinalizarCompra' || 
    (Platform.OS === 'web' && typeof window !== 'undefined' && window.location.pathname.includes('/checkout'));
  // Estados para el tamaño de pantalla
  const [screenWidth, setScreenWidth] = useState(Dimensions.get('window').width);
  const isSmallScreen = screenWidth < 940;
  const isExtraSmallScreen = screenWidth < 576;
  const isSmallWeb = Platform.OS === 'web' && screenWidth < 768;
  // Actualizar tamaño de pantalla cuando cambia dimensiones
  useEffect(() => {
    const updateLayout = ({ window }) => {
      setScreenWidth(window.width);
    };

    // Manejo compatible tanto con API antigua como nueva de Dimensions
    const subscription = Dimensions.addEventListener('change', updateLayout);

    // Limpieza del listener al desmontar
    return () => {
      // Para versiones modernas de React Native
      if (subscription?.remove) {
        subscription.remove();
      }
      // Compatibilidad con versiones antiguas (React Native Web)
      else if (typeof Dimensions.removeEventListener === 'function') {
        Dimensions.removeEventListener('change', updateLayout);
      }
    };
  }, []);

  const [showLogin, setShowLogin] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchContainerRef = useRef(null);
  const searchContainerMobileRef = useRef(null);
  // Usar el contexto del carrito centralizado
  const { productos, carritoVisible, setCarritoVisible, toggleCarrito, totales } = useCart();
  
  // Calculamos la cantidad de productos del carrito desde el contexto
  const cantidadEnCarrito = productos ? productos.length : 0;

  // Usar el total que viene de la API (dsResultado.total[0].total)
  const totalCarrito = totales?.total || 0;

  const { setLoading, setDepartmentName, setCategoryName, setSubcategoryName, setSearchText,
    setDepartmentId, setCategoryId, setSubcategoryId,} = useProducts();

  const handleSelectSuggestion = (plu) => {
    const pluText = String(plu);
    if (!pluText || pluText.trim() === '') return;
    navigateToProducts(pluText, null, null, null);
  };

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    if (!isSearchFocused) return;

    const onPointerDown = (e) => {
      try {
        const t = e?.target;
        const inDesktop = !!(searchContainerRef.current && t && searchContainerRef.current.contains && searchContainerRef.current.contains(t));
        const inMobile = !!(searchContainerMobileRef.current && t && searchContainerMobileRef.current.contains && searchContainerMobileRef.current.contains(t));
        if (!inDesktop && !inMobile) {
          setIsSearchFocused(false);
        }
      } catch (_) {
        setIsSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown, true);
    document.addEventListener('touchstart', onPointerDown, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown, true);
      document.removeEventListener('touchstart', onPointerDown, true);
    };
  }, [isSearchFocused]);

  // Mostrar el carrito por defecto en web (excepto en la pantalla de finalizar compra)
  useEffect(() => {
    if (Platform.OS === 'web' && !isFinalizarCompra) {
      setCarritoVisible(false);
    }
  }, [isFinalizarCompra, setCarritoVisible]);

  // Registrar usuario en Google Analytics cuando est� autenticado (solo una vez por sesi�n)
  useEffect(() => {
    if (nombreUsuario && nombreUsuario.length > 0 && codUsu) {
      // Verificar si ya se envió el evento en esta sesión
      const sessionKey = `ga_login_tracked_${codUsu}`;
      const alreadyTracked = Platform.OS === 'web' && typeof sessionStorage !== 'undefined'
        ? sessionStorage.getItem(sessionKey)
        : false;
      
      if (!alreadyTracked) {
        trackUserLogin(codUsu);
        // Marcar como enviado en esta sesión
        if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem(sessionKey, 'true');
        }
      }
    }
  }, [nombreUsuario, codUsu]);
  const handleUserButton = () => {
    if (codUsu) {
      // Redirigir directamente a la página de perfil en lugar de mostrar el menú
      if (Platform.OS === 'web') {
        window.location.href = '/perfil';
      } else {
        navigation.navigate('Perfil');
      }
    } else {
      setShowLogin(true);
    }
  };

  // Handler para abrir/cerrar categorías (cierra el carrito si está abierto)
  const handleToggleCategories = () => {
    if (!showCategories) {
      // Si vamos a abrir categorías, cerrar el carrito
      setCarritoVisible(false);
    }
    setShowCategories(!showCategories);
  };

  // Handler para abrir/cerrar carrito (cierra las categorías si están abiertas)
  const handleToggleCarrito = () => {
    if (!carritoVisible) {
      // Si vamos a abrir el carrito, cerrar las categorías
      setShowCategories(false);
    }
    toggleCarrito();
  };

  // Función para navegar a la página de productos con filtros
  const navigateToProducts = (searchText, deptId, catId, subcatId) => {
    // Limpiar todos los estados primero para evitar valores mezclados
    // Si estamos navegando por búsqueda de texto, limpiamos los valores de categoría
    if (searchText && searchText.trim() !== '') {
      setSearchText(searchText);
      // Limpiar valores de categorías cuando buscamos por texto
      setDepartmentId('0');
      setCategoryId('0');
      setSubcategoryId('0');
      setDepartmentName('');
      setCategoryName('');
      setSubcategoryName('');
    }
    // Si estamos navegando por categorías, limpiamos el texto de búsqueda
    else if (deptId || catId || subcatId) {
      setSearchText('');
      // Establecer los IDs solo si tienen valores significativos
      if (deptId && deptId !== 0) setDepartmentId(deptId);
      if (catId && catId !== 0) setCategoryId(catId);
      if (subcatId && subcatId !== 0) setSubcategoryId(subcatId);
    }

    // Navegar a la página de productos
    if (PlatformDetector.isWeb() && typeof window !== 'undefined') {
      // En web, construir URL solo con parámetros que tengan valores significativos
      const urlParams = new URLSearchParams();

      // Parámetros de búsqueda y categorías
      if (deptId && deptId !== 0) urlParams.append('D', deptId);
      if (catId && catId !== 0) urlParams.append('C', catId);
      if (subcatId && subcatId !== 0) urlParams.append('S', subcatId);
      if (searchText && searchText.trim() !== '') urlParams.append('searchText', searchText);

      // Incluir información del usuario si está logueado
      if (codUsu) urlParams.append('codUsu', codUsu);
      if (mail) urlParams.append('mail', mail);

      const url = `/productos${urlParams.toString() ? `?${urlParams.toString()}` : ''}`;

      // Verificar si ya estamos en /productos
      const isOnProductsPage = window.location.pathname === '/productos';
      
      if (isOnProductsPage) {
        // Si ya estamos en productos, solo actualizar la URL y disparar evento
        window.history.pushState({}, '', url);
        window.dispatchEvent(new Event('navigationChange'));
      } else {
        // Si estamos en otra p�gina, navegar usando window.location
        window.location.href = url;
      }
    } else {
      // En plataformas móviles, crear objeto de parámetros solo con valores significativos
      const params = {};

      // Parámetros de búsqueda y categorías
      if (deptId && deptId !== 0) params.D = deptId;
      if (catId && catId !== 0) params.C = catId;
      if (subcatId && subcatId !== 0) params.S = subcatId;
      if (searchText && searchText.trim() !== '') params.searchText = searchText;

      // Incluir información del usuario si está logueado
      if (codUsu) params.codUsu = codUsu;
      if (mail) params.mail = mail;

      // Navegar solo con los parámetros necesarios
      navigation.navigate('Productos', params);
    }

    // Mostrar indicador de carga
    setLoading(true);
  };

  // Definir cuál diseño mostrar basado en el tamaño de la pantalla
  const isMobileView = Platform.OS !== 'web' || isSmallScreen;

  return (
    <>
      {/* Header principal con dos secciones: roja y blanca */}
      <View style={styles.mainContainer}>
        {/* Logo superpuesto que ocupa ambas franjas (solo desktop) */}
        {!isMobileView && (
          <TouchableOpacity onPress={() => navigation.navigate('Home')} style={[styles.logoOverlay, isFinalizarCompra && styles.logoOverlayCheckout]}>
            <Image
              source={require('../../../assets/logoPin.png')}
              style={[styles.logoLarge, isFinalizarCompra && styles.logoLargeCheckout]}
              resizeMode="contain"
            />
          </TouchableOpacity>
        )}
        {/* Sección superior roja */}
        <View style={[
          styles.container,
          !isMobileView && styles.containerWithLogoPadding,
          isSmallWeb && styles.containerMobilePadding
        ]}>
          {isMobileView ? (
            <>
              {/* Logo móvil */}
              <TouchableOpacity onPress={() => navigation.navigate('Home')} style={styles.logoContainer}>
                <Image
                  source={require('../../../assets/logoPin.png')}
                  style={[styles.logo, styles.logoMobile]}
                  resizeMode="contain"
                />
              </TouchableOpacity>
              {/* Menú móvil */}
              <TouchableOpacity
                style={styles.mobileMenuButton}
                onPress={handleToggleCategories}
              >
                <MaterialIcons name="menu" size={22} color="white" />
              </TouchableOpacity>
              {/* Búsqueda móvil */}
              <View ref={searchContainerMobileRef} style={styles.searchContainerMobile}>
                <TextInput
                  style={styles.searchInput}
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                  placeholder="Buscar..."
                  placeholderTextColor="#999"
                  returnKeyType="search"
                  blurOnSubmit={true}
                  enterKeyHint="search"
                  onFocus={() => {
                    setIsSearchFocused(true);
                  }}
                  onBlur={() => {
                    if (Platform.OS !== 'web') {
                      setTimeout(() => setIsSearchFocused(false), 120);
                    }
                  }}
                  onSubmitEditing={() => {
                    if (searchQuery.trim()) {
                      navigateToProducts(searchQuery, null, null, null);
                    }
                  }}
                  onKeyPress={(e) => {
                    // Fallback para móvil web donde onSubmitEditing puede no funcionar
                    if (Platform.OS === 'web' && e.nativeEvent.key === 'Enter' && searchQuery.trim()) {
                      navigateToProducts(searchQuery, null, null, null);
                    }
                  }}
                />
                <DesplegableProductos
                  searchQuery={searchQuery}
                  isFocused={isSearchFocused}
                  onSelect={handleSelectSuggestion}
                />
              </View>

              {/* Botones móviles */}
              <View style={styles.mobileButtonsContainer}>
                <TouchableOpacity style={styles.iconButton} onPress={handleUserButton}>
                  <Ionicons name="person-outline" size={22} color="white" />
                </TouchableOpacity>
                {!isFinalizarCompra && (
                  <TouchableOpacity style={styles.iconButton} onPress={handleToggleCarrito}>
                    <View style={{flexDirection: 'row', alignItems: 'center'}}>
                      <Ionicons name="cart-outline" size={22} color="white" />
                      {cantidadEnCarrito > 0 && (
                        <View style={styles.badgeContainer}>
                          <Text style={styles.badgeText}>{cantidadEnCarrito}</Text>
                        </View>
                      )}
                    </View>
                  </TouchableOpacity>
                )}
              </View>
            </>
          ) : (
            <>
              {/* Categorías */}
              <View style={styles.categorySection}>
                <TouchableOpacity
                  style={styles.categoryButton}
                  onPress={handleToggleCategories}
                >
                  <View style={styles.categoryButtonInner}>
                    <MaterialIcons name="menu" size={24} color="white" />
                    <Text style={styles.categoryButtonText}>CATEGORIAS</Text>
                    <MaterialIcons
                      name={showCategories ? "keyboard-arrow-up" : "keyboard-arrow-down"}
                      size={24}
                      color="white"
                    />
                  </View>
                </TouchableOpacity>
              </View>
              {/* Búsqueda desktop */}
              <View style={styles.centerSection}>
                <View ref={searchContainerRef} style={[styles.searchContainer, !isMobileView && styles.searchContainerDesktop]}>
                  <TextInput
                    style={styles.searchInput}
                    value={searchQuery}
                    onChangeText={setSearchQuery}
                    placeholder="Buscar productos"
                    placeholderTextColor="#999"
                    returnKeyType="search"
                    blurOnSubmit={true}
                    enterKeyHint="search"
                    onFocus={() => {
                      setIsSearchFocused(true);
                    }}
                    onBlur={() => {
                      if (Platform.OS !== 'web') {
                        setTimeout(() => setIsSearchFocused(false), 120);
                      }
                    }}
                    onSubmitEditing={() => {
                      if (searchQuery.trim()) {
                        navigateToProducts(searchQuery, null, null, null);
                      }
                    }}
                    onKeyPress={(e) => {
                      if (Platform.OS === 'web' && e.nativeEvent.key === 'Enter' && searchQuery.trim()) {
                        navigateToProducts(searchQuery, null, null, null);
                      }
                    }}
                  />
                  <TouchableOpacity style={styles.searchButton} onPress={() => {
                    if (searchQuery.trim()) {
                      navigateToProducts(searchQuery, null, null, null);
                    }
                  }}>
                    <Ionicons name="search" size={24} color="#999" />
                  </TouchableOpacity>

                  <DesplegableProductos
                    searchQuery={searchQuery}
                    isFocused={isSearchFocused}
                    onSelect={handleSelectSuggestion}
                  />
                </View>
              </View>

              {/* Usuario */}
              <View style={styles.userSection}>
                <TouchableOpacity style={styles.userButton} onPress={handleUserButton}>
                  <Ionicons name="person-outline" size={24} color="white" />
                  <Text style={styles.buttonText}>
                    {nombreUsuario && nombreUsuario.length > 0 ? nombreUsuario : 'Mi cuenta'}
                  </Text>
                </TouchableOpacity>
              </View>
              {/* Carrito */}
              {!isFinalizarCompra && (
                <View style={styles.cartSection}>
                  <View style={styles.cartButtonWrapper}>
                    <TouchableOpacity style={styles.cartButton} onPress={handleToggleCarrito}>
                      <Ionicons name="cart-outline" size={24} color="white" />
                      <Text style={styles.buttonText}>Mi carrito {cantidadEnCarrito > 0 && `(${cantidadEnCarrito})`}</Text>
                      <MaterialIcons name="keyboard-arrow-right" size={24} color="white" />
                    </TouchableOpacity>
                    {/* Indicador del total del carrito - Solo en pantallas grandes */}
                    {cantidadEnCarrito > 0 && (
                      <View style={styles.totalCarritoContainer}>
                        <Text style={styles.totalCarritoText}>
                         $ {formatPrecio(totalCarrito, true)}
                        </Text>
                      </View>
                    )}
                  </View>
                </View>
              )}
            </>
          )}

        </View>

        {/* Sección inferior blanca con selector de sucursal y botones - Ocultar en checkout */}
        {!isFinalizarCompra && (
        <View style={[styles.whiteSection, isExtraSmallScreen && styles.whiteSectionSmall, !isMobileView && styles.whiteSectionWithLogoPadding]}>
          <View style={styles.bannerBotonesContainer}>
            {isMobileView ? (
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.bannerButtonsScrollContent}
              >
                <BannerManager>
                  <BannerBotonesHeader />
                </BannerManager>
              </ScrollView>
            ) : (
              <BannerManager>
                <BannerBotonesHeader />
              </BannerManager>
            )}
          </View>
        </View>
      )}
      </View>

      {/* Overlay para cerrar carrito al hacer click fuera */}
      {Platform.OS === 'web' && carritoVisible && (
        <TouchableOpacity
          style={styles.carritoOverlay}
          activeOpacity={1}
          onPress={() => setCarritoVisible(false)}
        />
      )}
      {/* Panel de carrito fijo a la derecha - Fuera del contenedor principal */}
      {Platform.OS === 'web' && carritoVisible && (
        <View
          style={[
            styles.carritoPanel,
            isExtraSmallScreen && styles.carritoPanelMobile
          ]}
        >
          <View style={[styles.carritoHeader, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', }]}>
            <Text style={styles.carritoTitle}>Mi Carrito</Text>
            <TouchableOpacity
              onPress={() => setCarritoVisible(false)}
              style={styles.closeButtonCarrito}
            >
              <MaterialIcons name="close" size={24} color={colores.textoPrincipal} style={{ marginRight: 10 }} />
            </TouchableOpacity>
          </View>
          <View style={styles.dividerLine} />
          <Carrito onClose={() => setCarritoVisible(false)} />
        </View>
      )}
      {showCategories && (
        isSmallScreen ? (
          <CategoriasMobil
            visible={showCategories}
            onClose={() => setShowCategories(false)}
            onCategorySelect={(deptId, categoryId, subcategoryId) => {
              navigateToProducts('', deptId, categoryId, subcategoryId);
              setShowCategories(false);
            }}
          />
        ) : (
          <Categorias
            visible={showCategories}
            onClose={() => setShowCategories(false)}
            onCategorySelect={(deptId, categoryId, subcategoryId) => {
              navigateToProducts('', deptId, categoryId, subcategoryId);
              setShowCategories(false);
            }}
          />
        )
      )}
      {showLogin && (
        <Login
          visible={showLogin}
          onClose={() => setShowLogin(false)}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  badgeContainer: {
    position: 'absolute',
    top: -8,
    right: -8,
    backgroundColor: colores.verde,
    borderRadius: 10,
    minWidth: 18,
    height: 18,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 3,
    borderWidth: 1,
    borderColor: colores.blanco,
  },
  badgeText: {
    color: colores.blanco,
    fontSize: 10,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  mainContainer: {
    flexDirection: 'column',
    width: '100%',
  },
  carritoOverlay: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    zIndex: 9999,
  },
  carritoPanel: {
    width: 350,
    maxWidth: '100vw',
    backgroundColor: colores.blanco,
    height: 'calc(100vh - 40px)',
    maxHeight: 'calc(100vh - 40px)',
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    right: 0,
    top: 40,
    zIndex: 10000,
    ...(Platform.OS === 'web'
      ? { boxShadow: '-2px 0px 3.84px rgba(0,0,0,0.25)' }
      : {
          shadowColor: '#000',
          shadowOffset: { width: -2, height: 0 },
          shadowOpacity: 0.25,
          shadowRadius: 3.84,
          elevation: 5,
        }
    ),
    paddingVertical: 10,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  carritoPanelMobile: {
    width: '100vw',
    left: 0,
    right: 0,
    top: 0,
    height: '100vh',
    maxHeight: '100vh',
    paddingTop: 10,
  },
  carritoTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colores.textoPrincipal,
    marginLeft: 20,
    marginleft: layout.contentPadding - 40,
  },
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 60,
    width: '100%',
    backgroundColor: colores.rojo,
    paddingHorizontal: layout.contentPadding,
    flexWrap: 'nowrap',
    justifyContent: 'space-between',
    zIndex: 950,
    position: 'relative',
  },
  containerMobilePadding: {
    paddingHorizontal: layout.contentPaddingMobile,
  },
  whiteSection: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 40,
    width: '100%',
    backgroundColor: colores.rojo,
    //borderBottomWidth: 1,
    borderBottomColor: colores.grisClaro,
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
    overflow: 'visible',
    zIndex: 800,
    position: 'relative',
  },
  whiteSectionSmall: {
    paddingHorizontal: layout.contentPaddingMobile,
    height: 'auto',
    minHeight: 40,
    minWidth: 315,

  },
  bannerBotonesContainer: {
    width: '100%',
    height: 40,
    flexShrink: 1,
    overflow: 'visible',
  },
  logoContainer: {
    height: 40,
    marginRight: 10,
    justifyContent: 'center',
  },
  logo: {
    height: 40,
  },
  logoMobile: {
    height: 30,
    width: 70,
  },
  categoryButton: {
    alignItems: 'center',
    justifyContent: 'center',
    height: 40,
    marginRight: 0,
  },
  categoryButtonInner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 10,
  },
  categoryButtonText: {
    color: colores.blanco,
    marginHorizontal: 5,
    fontSize: 16, 
    fontWeight: 'bold',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colores.blanco,
    borderRadius: 20,
    height: 40,
    marginHorizontal: 0,
    marginTop: 0,
    marginBottom: 0,
    maxWidth: '100%',
    width: '100%',
    overflow: 'visible',
    position: 'relative',
  },
  searchContainerDesktop: {
    flexGrow: 0,
    flexShrink: 0,
    width: '100%',
    maxWidth: '100%',
    alignSelf: 'center',
  },
  searchSuggestionsDropdown: {
    position: 'absolute',
    top: 44,
    left: 0,
    right: 0,
    backgroundColor: colores.blanco,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E5E5',
    zIndex: 2000,
    overflow: 'visible',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 8px 20px rgba(0,0,0,0.18)' }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 6 },
          shadowOpacity: 0.18,
          shadowRadius: 10,
          elevation: 10,
        }
    ),
  },
  searchSuggestionCard: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
    backgroundColor: colores.blanco,
  },
  searchSuggestionImg: {
    width: 44,
    height: 44,
    marginRight: 10,
    backgroundColor: 'transparent',
  },
  searchSuggestionInfo: {
    flex: 1,
    minWidth: 0,
  },
  searchSuggestionTitle: {
    fontSize: 13,
    fontFamily: fuentes.medium,
    color: colores.textoPrincipal,
  },
  searchSuggestionMetaRow: {
    marginTop: 4,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  searchSuggestionPrice: {
    fontSize: 13,
    fontFamily: fuentes.bold,
    color: colores.rojo,
  },
  searchSuggestionBadges: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  searchSuggestionBadgeOferta: {
    backgroundColor: colores.verde,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 10,
  },
  searchSuggestionBadgeCombo: {
    backgroundColor: colores.rojo,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 10,
  },
  searchSuggestionBadgeText: {
    color: colores.blanco,
    fontFamily: fuentes.bold,
    fontSize: 10,
    letterSpacing: 0.2,
  },
  searchContainerMobile: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colores.blanco,
    borderRadius: 15,
    height: 34,
    marginHorizontal: 8,
    overflow: 'visible',
    position: 'relative',
  },
  searchInput: {
    flex: 1,
    height: '100%',
    paddingHorizontal: 15,
    fontSize: 14,
    color: colores.textoPrincipal,
  },
  searchButton: {
    height: 40,
    width: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobileMenuButton: {
    width: 34,
    height: 34,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 5,
  },
  mobileButtonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconButton: {
    width: 34,
    height: 34,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 5,
  },
  userButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  cartButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  buttonText: {
    marginLeft: 5,
    //marginRight: 5,
    color: colores.blanco,
    fontWeight: 'bold',
  },
  closeButtonCarrito: {
    padding: 5,
    marginRight: 20,
  },
  dividerLine: {
    height: 2,
    backgroundColor: colores.rojo,
    width: '100%',
    marginBottom: 10
  },
  userSection: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cartSection: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  cartButtonWrapper: {
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
  },
  totalCarritoContainer: {
    position: 'absolute',
    top: 26,
    left: 0,
    paddingHorizontal: 8,
    paddingVertical: 3,
    backgroundColor: '#429745',
    borderRadius: 12,
  },
  totalCarritoText: {
    color: colores.blanco,
    fontSize: 14,
    fontWeight: '600',
    letterSpacing: 0.3,
  },
  logoOverlay: {
    position: 'absolute',
    left: layout.contentPadding - 0,
    top: 0,
    height: 100, // 60 (container) + 40 (whiteSection)
    width: 150,
    zIndex: 1001,
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoOverlayCheckout: {
    top: -20,
  },
  logoLarge: {
    height: '100%',
    width: '100%',
  },
  logoLargeCheckout: {
    transform: [{ scale: 0.9 }],
    marginLeft: 30,
  },
  containerWithLogoPadding: {
    paddingLeft: layout.contentPadding + 150, // contentPadding + logo width
  },
  whiteSectionWithLogoPadding: {
    paddingLeft: layout.contentPadding + 180, // contentPadding + logo width
    paddingRight: 70,
  },
  centerSection: {
    flexGrow: 0,
    flexShrink: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
    width: '45%',
  },
  leftSection: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  categorySection: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  bannerButtonsScrollContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minWidth: '100%',
    paddingHorizontal: 8,
    gap: 8,
  }
});

export default HeaderRojo;
